import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'G1-3'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  GC01: {
    scene: '🏛️ 박물관 견학. 다양한 입체 조형물을 관찰하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '선생님, 이 조형물은 각기둥인가요 각뿔인가요? 어떻게 구분해요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '좋은 질문! 각기둥은 두 밑면이 평행하고 합동인 다각형이에요. 옆면은 모두 직사각형이고요.' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '그럼 각뿔은요? 피라미드처럼 뾰족한 거잖아요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 각뿔은 밑면이 하나인 다각형이고, 옆면은 삼각형이에요. 꼭짓점이 하나 있죠. 다면체의 면·꼭짓점·모서리 수는 오일러 공식 F+V-E=2로 연결돼요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '각기둥: 두 밑면 합동+평행, 옆면=직사각형 | 각뿔: 밑면 하나, 옆면=삼각형 | F+V-E=2' },
    ],
    reveal: '각기둥 옆면=직사각형 | 각뿔 옆면=삼각형 | 오일러: F+V-E=2',
    revealEmoji: '🏛️',
  },
  GC02: {
    scene: '🏭 제조 공장. 원기둥·원뿔·구 형태의 제품을 생산하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '회전체는 어떻게 만들어지는 건가요?' },
      { speaker: 'teacher', emoji: '🔧', name: '기술자', text: '평면도형을 한 직선(축)을 중심으로 회전시키면 만들어져요! 직사각형을 회전하면 원기둥, 직각삼각형을 회전하면 원뿔이 됩니다.' },
      { speaker: 'friend', emoji: '😮', name: '수아', text: '구는요? 반원을 지름을 축으로 회전시키면 구가 되겠네요!' },
      { speaker: 'teacher', emoji: '🔧', name: '기술자', text: '완벽해요! 회전체의 단면 모양도 중요해요. 회전축에 평행한 단면은 직사각형(원기둥), 수직한 단면은 원이에요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '원기둥: 직사각형 회전 | 원뿔: 직각삼각형 회전 | 구: 반원 회전' },
    ],
    reveal: '원기둥=직사각형 회전 | 원뿔=직각삼각형 회전 | 구=반원 회전',
    revealEmoji: '🔄',
  },
  GC03: {
    scene: '🎁 포장 공장. 선물 상자와 캔의 포장지 크기를 계산하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '이 원기둥 캔을 포장하려면 종이가 얼마나 필요한가요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '겉넓이를 구해야 해요! 원기둥 겉넓이 = 2πr² + 2πrh. 두 원형 밑면과 옆면(직사각형)의 합이에요.' },
      { speaker: 'friend', emoji: '😄', name: '수아', text: '원뿔은요? 전개도를 펼치면 부채꼴이 되잖아요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 원뿔 겉넓이 = πr² + πrl. 밑면원 + 옆면(부채꼴)이에요. l은 모선 길이예요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '원기둥: 2πr²+2πrh | 원뿔: πr²+πrl (l=모선) | 구: 4πr²' },
    ],
    reveal: '원기둥 겉넓이=2πr²+2πrh | 원뿔=πr²+πrl | 구=4πr²',
    revealEmoji: '📦',
  },
  GC04: {
    scene: '🚰 수도 공사. 파이프와 탱크의 용량을 계산하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '이 원기둥 물탱크에 물이 얼마나 들어갈까요?' },
      { speaker: 'teacher', emoji: '🔧', name: '기술자', text: '부피를 구해야 해요! 원기둥 부피 = πr²h. 밑면 넓이 × 높이예요. 각기둥도 같은 원리예요!' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '원뿔은 원기둥의 1/3이라고 들었어요. 왜요?' },
      { speaker: 'teacher', emoji: '🔧', name: '기술자', text: '같은 밑면과 높이를 가진 원뿔은 원기둥 부피의 정확히 1/3이에요! 각뿔도 각기둥의 1/3이고요. 구 부피는 (4/3)πr³이에요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '원기둥: πr²h | 원뿔: (1/3)πr²h | 구: (4/3)πr³ | 각기둥=Sh, 각뿔=(1/3)Sh' },
    ],
    reveal: '원기둥=πr²h | 원뿔=(1/3)πr²h | 구=(4/3)πr³',
    revealEmoji: '🚰',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'GC01',
    title: '다면체의 분류 (각기둥·각뿔)',
    subtitle: '다면체를 면의 수와 모양으로 분류하고 각기둥·각뿔의 성질을 이해한다',
    icon: '🏛️',
    color: 'bg-sky-500',
    lightColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    textColor: 'text-sky-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['다면체', '각기둥', '각뿔', '오일러'],
    objective: '다면체를 분류하고, 각기둥과 각뿔의 면·모서리·꼭짓점의 수를 구하며 오일러 공식을 확인한다.',
    concept: `**다면체의 분류 (각기둥·각뿔)**

📌 다면체란?
• 면이 모두 다각형인 입체도형
• 면(Face), 꼭짓점(Vertex), 모서리(Edge)로 구성

📌 각기둥 (n각기둥)
• 두 밑면: 합동이고 평행한 n각형
• 옆면: 직사각형 n개
• 면의 수: n+2개
• 꼭짓점의 수: 2n개
• 모서리의 수: 3n개

| 각기둥 | 면 | 꼭짓점 | 모서리 |
|--------|----|---------|----|
| 삼각기둥 | 5 | 6 | 9 |
| 사각기둥 | 6 | 8 | 12 |
| 오각기둥 | 7 | 10 | 15 |
| n각기둥 | n+2 | 2n | 3n |

📌 각뿔 (n각뿔)
• 밑면: n각형 1개
• 옆면: 삼각형 n개
• 면의 수: n+1개
• 꼭짓점의 수: n+1개
• 모서리의 수: 2n개

| 각뿔 | 면 | 꼭짓점 | 모서리 |
|------|----|---------|----|
| 삼각뿔 | 4 | 4 | 6 |
| 사각뿔 | 5 | 5 | 8 |
| 오각뿔 | 6 | 6 | 10 |
| n각뿔 | n+1 | n+1 | 2n |

📌 오일러 공식
• F + V - E = 2 (모든 다면체에서 성립!)
• F: 면의 수, V: 꼭짓점의 수, E: 모서리의 수

⚠️ 주의
• 각기둥과 각뿔은 밑면 모양으로 이름이 결정됨
• 정다면체는 정사면체·정육면체·정팔면체·정십이면체·정이십면체 5가지만 존재`,
    keyPoints: [
      'n각기둥: 면 n+2, 꼭짓점 2n, 모서리 3n',
      'n각뿔: 면 n+1, 꼭짓점 n+1, 모서리 2n',
      '각기둥 옆면=직사각형, 각뿔 옆면=삼각형',
      '오일러 공식: F + V - E = 2',
    ],
    examples: [
      {
        question: '사각기둥의 면·꼭짓점·모서리의 수를 구하고\n오일러 공식이 성립하는지 확인하시오.',
        answer: '면=6, 꼭짓점=8, 모서리=12',
        explanation: 'n=4: 면=4+2=6, 꼭짓점=2×4=8, 모서리=3×4=12\n오일러: 6+8-12=2 ✓',
      },
      {
        question: '면이 7개인 각기둥은 몇 각기둥인가?\n꼭짓점과 모서리의 수도 구하시오.',
        answer: '오각기둥, 꼭짓점=10, 모서리=15',
        explanation: 'n+2=7 → n=5 → 오각기둥\n꼭짓점=2×5=10, 모서리=3×5=15\n오일러: 7+10-15=2 ✓',
      },
    ],
    practice: {
      question: '오각뿔의 면·꼭짓점·모서리의 수를 구하고\n오일러 공식 F+V-E=2가 성립하는지 확인하시오.',
      answer: '면=6, 꼭짓점=6, 모서리=10, 6+6-10=2 ✓',
      hint: 'n각뿔: 면=n+1, 꼭짓점=n+1, 모서리=2n. n=5 대입!',
      steps: [
        'n=5 (오각뿔)',
        '면의 수: 5+1 = 6개',
        '꼭짓점의 수: 5+1 = 6개',
        '모서리의 수: 2×5 = 10개',
        '오일러: 6+6-10 = 2 ✓',
      ],
    },
    misconceptions: ['GP01'],
  },
  {
    id: 'GC02',
    title: '회전체 (원기둥·원뿔·구)',
    subtitle: '평면도형의 회전으로 생기는 입체도형을 이해하고 단면을 파악한다',
    icon: '🔄',
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    textColor: 'text-violet-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['회전체', '원기둥', '원뿔', '구', '단면'],
    objective: '회전체가 어떤 평면도형을 회전하여 만들어지는지 이해하고, 회전축에 수직·평행한 단면을 파악한다.',
    concept: `**회전체 (원기둥·원뿔·구)**

📌 회전체란?
• 평면도형을 한 직선(회전축)을 중심으로 1회전하여 만든 입체도형
• 항상 곡면을 포함

📌 원기둥
• 만드는 도형: 직사각형을 한 변을 축으로 회전
• 두 밑면: 합동인 원 2개
• 옆면: 곡면 (전개하면 직사각형)
• 회전축에 평행한 단면: 직사각형
• 회전축에 수직한 단면: 원

📌 원뿔
• 만드는 도형: 직각삼각형을 직각 변(높이)을 축으로 회전
• 밑면: 원 1개
• 옆면: 곡면 (전개하면 부채꼴)
• 모선: 꼭짓점에서 밑면 원 위의 점까지의 거리
• 회전축에 평행한 단면: 이등변삼각형
• 회전축에 수직한 단면: 원

📌 구
• 만드는 도형: 반원을 지름을 축으로 회전
• 곡면만으로 이루어진 회전체
• 어떤 방향으로 잘라도 단면은 원
• 중심에서의 단면: 가장 큰 원(대원)

📌 회전체 비교표
| 회전체 | 회전 도형 | 밑면 | 옆면 |
|--------|----------|------|------|
| 원기둥 | 직사각형 | 원×2 | 직사각형(곡면) |
| 원뿔 | 직각삼각형 | 원×1 | 부채꼴(곡면) |
| 구 | 반원 | 없음 | 구면 |

⚠️ 주의
• 회전체는 다면체가 아님 (곡면 포함)
• 원기둥·원뿔의 전개도 꼭 이해하기`,
    keyPoints: [
      '원기둥: 직사각형 회전, 단면=직사각형(평행)/원(수직)',
      '원뿔: 직각삼각형 회전, 단면=이등변삼각형(평행)/원(수직)',
      '구: 반원 회전, 모든 단면=원',
      '모선 l = 원뿔 꼭짓점 ~ 밑면 원 위의 점',
    ],
    examples: [
      {
        question: '다음 평면도형을 직선 l을 축으로 회전할 때\n어떤 회전체가 만들어지는지 쓰시오.\n① 직사각형(가로 r, 세로 h)  ② 직각삼각형(밑변 r, 높이 h)',
        answer: '① 원기둥 (반지름 r, 높이 h)\n② 원뿔 (반지름 r, 높이 h)',
        explanation: '① 직사각형의 한 변을 축으로 회전 → 원기둥\n② 직각삼각형의 직각 변을 축으로 회전 → 원뿔',
      },
      {
        question: '원뿔을 회전축에 평행한 평면으로 자를 때\n단면의 모양은 무엇인가?',
        answer: '이등변삼각형',
        explanation: '원뿔의 회전축을 포함하는 단면은 이등변삼각형.\n두 모선이 같으므로 이등변삼각형!',
      },
    ],
    practice: {
      question: '반지름이 4cm, 높이가 3cm인 원기둥이 있다.\n① 회전축에 수직한 단면의 모양과 넓이\n② 회전축에 평행한 단면의 모양과 넓이를 구하시오.',
      answer: '① 원, 16π cm²  ② 직사각형, 24 cm²',
      hint: '① 수직 단면=원(반지름 4). ② 평행 단면=직사각형(가로=지름=8, 세로=높이=3)',
      steps: [
        '① 수직 단면: 원 (반지름=4cm)',
        '   넓이 = π×4² = 16π cm²',
        '② 평행 단면: 직사각형 (가로=2×4=8cm, 세로=3cm)',
        '   넓이 = 8×3 = 24 cm²',
      ],
    },
    misconceptions: ['GP02'],
  },
  {
    id: 'GC03',
    title: '겉넓이 계산',
    subtitle: '각기둥·각뿔·원기둥·원뿔·구의 겉넓이를 전개도를 이용하여 구한다',
    icon: '📦',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    textColor: 'text-orange-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['겉넓이', '전개도', '원기둥', '원뿔', '각기둥'],
    objective: '입체도형의 전개도를 이해하고, 각기둥·원기둥·원뿔·구의 겉넓이를 공식으로 구할 수 있다.',
    concept: `**겉넓이 계산**

📌 겉넓이란?
• 입체도형의 모든 면의 넓이의 합
• 전개도를 펼쳐서 계산

📌 각기둥 겉넓이
• 겉넓이 = (밑면 넓이 × 2) + (옆면 넓이의 합)
• 옆면: 직사각형들 (가로=밑면 둘레, 세로=높이)
• 사각기둥: 2ab + 2(a+b)h

📌 원기둥 겉넓이
• 겉넓이 = 2πr² + 2πrh
• 밑면 원 2개 + 옆면 직사각형(가로=2πr, 세로=h)

📌 원뿔 겉넓이
• 겉넓이 = πr² + πrl
• 밑면 원 + 옆면 부채꼴
• l: 모선의 길이 (꼭짓점~밑원 위의 점)
• 옆면(부채꼴) 넓이 = πrl

📌 구 겉넓이
• 겉넓이 = 4πr²
• 반지름이 같은 원의 넓이의 4배!

📌 겉넓이 공식 정리
| 도형 | 겉넓이 |
|------|--------|
| 원기둥 | 2πr² + 2πrh |
| 원뿔 | πr² + πrl |
| 구 | 4πr² |

⚠️ 주의
• 원뿔의 l(모선)과 h(높이) 구분!
• l = √(r² + h²) (피타고라스 정리)
• 구는 밑면이 없음 → 4πr² 하나만`,
    keyPoints: [
      '원기둥 겉넓이 = 2πr² + 2πrh',
      '원뿔 겉넓이 = πr² + πrl (l=모선)',
      '구 겉넓이 = 4πr²',
      '모선 l = √(r²+h²) (직각삼각형)',
    ],
    examples: [
      {
        question: '반지름 3cm, 높이 5cm인 원기둥의 겉넓이를 구하시오.',
        answer: '48π cm²',
        explanation: '겉넓이 = 2πr² + 2πrh\n= 2π×9 + 2π×3×5\n= 18π + 30π = 48π cm²',
      },
      {
        question: '밑면 반지름 4cm, 모선 6cm인 원뿔의 겉넓이를 구하시오.',
        answer: '40π cm²',
        explanation: '겉넓이 = πr² + πrl\n= π×16 + π×4×6\n= 16π + 24π = 40π cm²',
      },
    ],
    practice: {
      question: '반지름 5cm인 구의 겉넓이를 구하고,\n반지름 5cm, 높이 12cm인 원기둥의 겉넓이도 구하시오.',
      answer: '구: 100π cm², 원기둥: 170π cm²',
      hint: '구: 4πr². 원기둥: 2πr²+2πrh. r=5 대입!',
      steps: [
        '구 겉넓이 = 4π×5² = 4π×25 = 100π cm²',
        '원기둥: 2π×5² + 2π×5×12',
        '= 2π×25 + 2π×60',
        '= 50π + 120π = 170π cm²',
      ],
    },
    misconceptions: ['GP03'],
  },
  {
    id: 'GC04',
    title: '부피 계산',
    subtitle: '각기둥·각뿔·원기둥·원뿔·구의 부피를 공식으로 구한다',
    icon: '🚰',
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
    textColor: 'text-teal-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['부피', '원기둥', '원뿔', '구', '각기둥', '각뿔'],
    objective: '각기둥·각뿔·원기둥·원뿔·구의 부피 공식을 이해하고, 각뿔·원뿔의 부피가 기둥의 1/3임을 설명할 수 있다.',
    concept: `**부피 계산**

📌 부피란?
• 입체도형이 차지하는 공간의 크기
• 단위: cm³, m³ 등

📌 각기둥·원기둥 부피
• 부피 = 밑면 넓이 × 높이
• 원기둥: V = πr²h
• 사각기둥: V = (가로×세로) × h

📌 각뿔·원뿔 부피
• 부피 = (1/3) × 밑면 넓이 × 높이
• 원뿔: V = (1/3)πr²h
• 각뿔: V = (1/3) × S × h
• 같은 밑면·높이의 기둥의 1/3!

📌 구 부피
• V = (4/3)πr³
• 반지름이 r인 구의 부피

📌 부피 공식 정리
| 도형 | 부피 |
|------|------|
| 원기둥 | πr²h |
| 원뿔 | (1/3)πr²h |
| 구 | (4/3)πr³ |
| 각기둥 | S×h (S=밑면 넓이) |
| 각뿔 | (1/3)S×h |

📌 원기둥과 원뿔 관계
• 같은 r, h: 원뿔 = 원기둥 × (1/3)
• 원기둥에 원뿔 3개가 꼭 맞음!

⚠️ 주의
• (1/3)을 빠뜨리지 않기! (뿔 계열은 모두 1/3)
• 구 공식 (4/3)πr³ 암기 필수
• h는 항상 밑면에 수직인 높이`,
    keyPoints: [
      '원기둥 부피 = πr²h',
      '원뿔 부피 = (1/3)πr²h (원기둥의 1/3)',
      '구 부피 = (4/3)πr³',
      '각뿔·원뿔은 항상 기둥의 1/3',
    ],
    examples: [
      {
        question: '반지름 3cm, 높이 4cm인 원뿔의 부피를 구하시오.\n같은 크기의 원기둥 부피도 비교하시오.',
        answer: '원뿔=12π cm³, 원기둥=36π cm³',
        explanation: '원뿔: (1/3)×π×9×4 = (1/3)×36π = 12π cm³\n원기둥: π×9×4 = 36π cm³\n원뿔=원기둥×1/3 확인! ✓',
      },
      {
        question: '반지름 6cm인 구의 부피를 구하시오.',
        answer: '288π cm³',
        explanation: 'V = (4/3)πr³ = (4/3)×π×216\n= (4×216÷3)×π = 288π cm³',
      },
    ],
    practice: {
      question: '밑면 한 변의 길이가 6cm이고\n높이가 9cm인 정사각뿔의 부피를 구하고,\n같은 밑면과 높이를 가진 사각기둥과 비교하시오.',
      answer: '사각뿔=108 cm³, 사각기둥=324 cm³ (3배 차이)',
      hint: '사각뿔: (1/3)×(6×6)×9. 사각기둥: (6×6)×9',
      steps: [
        '밑면 넓이 = 6×6 = 36 cm²',
        '사각뿔 부피 = (1/3)×36×9 = (1/3)×324 = 108 cm³',
        '사각기둥 부피 = 36×9 = 324 cm³',
        '비교: 324 ÷ 108 = 3 → 기둥은 뿔의 3배! ✓',
      ],
    },
    misconceptions: ['GP04'],
  },
]

// ── 하위 컴포넌트 ────────────────────────────────────────────────────────────
function PracticePanel({ practice }: { practice: typeof STEPS[0]['practice'] }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-sky-50 border border-sky-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-sky-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-xl text-xs text-sky-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-sky-50 rounded-lg p-3">
            <p className="text-xs font-bold text-sky-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-sky-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-sky-100 text-sky-800',
    teacher: 'bg-blue-100 text-blue-800',
    friend: 'bg-indigo-100 text-indigo-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-sky-50 to-white border border-sky-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-sky-100 rounded-full">
          <div className="h-1 bg-sky-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      {!revealed ? (
        <div className="flex gap-2">
          {lineIdx < story.lines.length - 1 && (
            <button onClick={() => setLineIdx(lineIdx + 1)}
              className="flex-1 py-2 bg-sky-100 hover:bg-sky-200 text-sky-700 font-semibold rounded-xl text-xs transition-colors">
              다음 대화 →
            </button>
          )}
          {lineIdx === story.lines.length - 1 && (
            <button onClick={() => setRevealed(true)}
              className="flex-1 py-2 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1">
              <Sparkles size={12} /> 핵심 공식 보기
            </button>
          )}
        </div>
      ) : (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
          <p className="text-lg text-center mb-1">{story.revealEmoji}</p>
          <p className="text-xs text-sky-800 font-mono text-center">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id)).slice(0, 3)
  if (visible.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
        <Brain size={12} /> AI 예상 취약 개념
      </p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0"><X size={11} /></button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const TOTAL_STEPS = STEPS.length

interface G1_3LearnPageProps { defaultStepId?: string | null }

export default function G1_3LearnPage({ defaultStepId }: G1_3LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(null)
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const learnProgress = useLearnProgress(UNIT_ID)
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  // ── 단계 목록 화면 ──────────────────────────────────────────────────────────
  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <PredictedFragmentPanel fragments={predictedFragments} />

        {/* 헤더 */}
        <div className="card bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-sky-100 rounded-2xl flex items-center justify-center text-2xl">🧊</div>
            <div>
              <h2 className="text-lg font-black text-slate-800">입체도형</h2>
              <p className="text-xs text-slate-500">G1-3 · 도형 영역</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-sky-100 rounded-full overflow-hidden">
              <div className="h-2 bg-sky-500 rounded-full transition-all"
                style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs font-bold text-sky-600 w-16 text-right">
              {completedCount}/{TOTAL_STEPS} 완료
            </span>
          </div>
        </div>

        {/* 단계 목록 */}
        <div className="space-y-2">
          {STEPS.map((step) => {
            const stepProg = learnProgress.steps[step.id]
            const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${done ? 'border-sky-200 bg-sky-50/30' : 'border-slate-200 hover:border-sky-300'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${done ? 'bg-sky-100' : step.lightColor}`}>
                    {done ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{step.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${step.difficultyColor}`}>
                        {step.difficulty}
                      </span>
                      {video && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex items-center gap-0.5">
                          <Youtube size={9} /> 영상
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{step.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-slate-400">{step.id}</span>
                    <ChevronRight size={15} className="text-slate-300" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {completedCount === TOTAL_STEPS && (
          <div className="card bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-300 text-center">
            <div className="text-3xl mb-2">🎉</div>
            <p className="text-sm font-bold text-sky-700">모든 개념 학습 완료!</p>
            <p className="text-xs text-slate-500 mt-1">실전 TEST에 도전해보세요</p>
          </div>
        )}
      </div>
    )
  }

  // ── 단계 상세 화면 ──────────────────────────────────────────────────────────
  const step = selectedStep
  const stepProg = learnProgress.steps[step.id]
  const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
  const story = STEP_STORIES[step.id]
  const video = getApprovedByTag(step.id)

  const tabs = [
    { id: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { id: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { id: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { id: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* 상단 헤더 */}
      <div className={`${step.lightColor} border-b ${step.borderColor} px-4 pt-3 pb-0 flex-shrink-0`}>
        <button onClick={() => setSelectedStep(null)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2 transition-colors">
          ← 목록으로
        </button>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{step.icon}</span>
          <div>
            <h2 className="text-base font-black text-slate-800">{step.title}</h2>
            <p className="text-xs text-slate-500">{step.subtitle}</p>
          </div>
          {done && <CheckCircle size={18} className="text-emerald-500 ml-auto" />}
        </div>
        {/* 태그 */}
        <div className="flex gap-1 flex-wrap mb-2">
          {step.tags.map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 bg-white/80 border border-sky-200 text-sky-700 rounded-full">
              #{tag}
            </span>
          ))}
        </div>
        {/* 탭 */}
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 px-3 py-2 rounded-t-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-white text-sky-700 border-t border-l border-r border-sky-200'
                  : 'text-slate-500 hover:text-slate-700'
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'story' && story && <StoryCard story={story} />}

        {activeTab === 'concept' && (
          <div className="space-y-4">
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
              <p className="text-xs font-bold text-sky-700 mb-1 flex items-center gap-1">
                <Target size={11} /> 학습 목표
              </p>
              <p className="text-sm text-slate-700">{step.objective}</p>
            </div>
            <div className="card">
              <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
                <BookOpen size={11} /> 핵심 개념
              </p>
              <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {step.concept}
              </div>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
              <p className="text-xs font-bold text-sky-700 mb-2 flex items-center gap-1">
                <Star size={11} /> 핵심 포인트
              </p>
              <ul className="space-y-1.5">
                {step.keyPoints.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <span className="text-sky-500 font-bold flex-shrink-0">✓</span>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
            {video && (
              <button onClick={() => setVideoModal({ stepId: step.id, label: step.title })}
                className="w-full flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                <Youtube size={16} className="text-red-600" />
                <span className="text-sm font-semibold text-red-700">개념 설명 영상 보기</span>
                <ArrowRight size={13} className="text-red-400 ml-auto" />
              </button>
            )}
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-3">
            <p className="text-xs font-bold text-slate-500">📝 예제 문제</p>
            {step.examples.map((ex, i) => (
              <ExampleCard key={i} example={ex} idx={i} />
            ))}
          </div>
        )}

        {activeTab === 'practice' && (
          <PracticePanel practice={step.practice} />
        )}

        {/* 완료 버튼 */}
        {!done ? (
          <button onClick={() => handleComplete(step)}
            className="w-full py-3 bg-sky-500 hover:bg-sky-600 text-white font-black rounded-2xl transition-colors flex items-center justify-center gap-2">
            <CheckCircle size={16} /> 학습 완료
          </button>
        ) : (
          <div className="flex items-center justify-center gap-2 py-3 bg-sky-50 border border-sky-200 rounded-2xl">
            <CheckCircle size={16} className="text-sky-500" />
            <span className="text-sm font-bold text-sky-700">완료된 단계입니다</span>
          </div>
        )}
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
